// Social links + phone, with per-tier visibility, stored so restricted ones are
// NOT in the world-readable user doc:
//   public      -> users/{uid}.socials  (world-readable)
//   connections -> users/{uid}/restrictedSocials/{id}  (rules: connected only)
//   top8        -> users/{uid}/restrictedSocials/{id}  (rules: in owner's Top 8)
//   private     -> users/{uid}/restrictedSocials/{id}  (rules: owner only)
// New socials default to private; the owner opens them up deliberately.
import { db, auth } from './firebase';
import { doc, getDoc, setDoc, deleteDoc, getDocs, collection, writeBatch, query, where } from 'firebase/firestore';

// The phone number is a single typed entry, stored under a reserved id so the
// socials sweep never touches it. A public (business) phone lives on the user
// doc as `phonePublic`; restricted phones live here, rules-gated like socials.
const PHONE_ID = '__phone__';

function genId() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function isPhoneDoc(d) {
  return d.id === PHONE_ID || d.data().type === 'phone';
}

// Owner editing: merge public socials (user doc) + their restricted socials.
// The phone doc is excluded; it has its own loader.
export async function loadOwnSocials(uid, publicSocials) {
  const byUrl = {};
  (publicSocials || []).forEach(s => {
    byUrl[s.url] = { id: s.id || genId(), url: s.url, visibility: s.visibility || 'public' };
  });
  try {
    const snap = await getDocs(collection(db, 'users', uid, 'restrictedSocials'));
    snap.forEach(d => {
      if (isPhoneDoc(d)) return;
      byUrl[d.data().url] = { id: d.id, url: d.data().url, visibility: d.data().visibility };
    });
  } catch { /* ignore */ }
  return Object.values(byUrl);
}

// Syncs the restricted subcollection (set kept, delete removed) and returns the
// public socials for the caller to store on the user doc. Never touches the
// phone doc.
export async function saveSocials(uid, socials) {
  const withIds = socials.map(s => ({ id: s.id || genId(), url: s.url, visibility: s.visibility || 'private' }));
  const restricted = withIds.filter(s => s.visibility !== 'public');
  const batch = writeBatch(db);
  const existing = await getDocs(collection(db, 'users', uid, 'restrictedSocials'));
  const keep = new Set(restricted.map(s => s.id));
  existing.forEach(d => { if (!isPhoneDoc(d) && !keep.has(d.id)) batch.delete(d.ref); });
  restricted.forEach(s => batch.set(doc(db, 'users', uid, 'restrictedSocials', s.id), { url: s.url, visibility: s.visibility }));
  await batch.commit();
  return withIds.filter(s => s.visibility === 'public').map(s => ({ url: s.url }));
}

// Viewer: the restricted URL socials this viewer is actually allowed to read.
// Each query matches a rule clause, so unauthorized tiers return nothing. The
// phone (and any private docs) are excluded.
export async function loadVisibleRestrictedSocials(ownerUid, { isOwn, isConnected, inTop8 }) {
  const out = [];
  try {
    if (isOwn) {
      const snap = await getDocs(collection(db, 'users', ownerUid, 'restrictedSocials'));
      snap.forEach(d => { if (!isPhoneDoc(d) && d.data().visibility !== 'private') out.push({ url: d.data().url, visibility: d.data().visibility }); });
      return out;
    }
    if (isConnected) {
      const snap = await getDocs(query(collection(db, 'users', ownerUid, 'restrictedSocials'), where('visibility', '==', 'connections')));
      snap.forEach(d => { if (!isPhoneDoc(d)) out.push({ url: d.data().url, visibility: 'connections' }); });
    }
    if (inTop8) {
      const snap = await getDocs(query(collection(db, 'users', ownerUid, 'restrictedSocials'), where('visibility', '==', 'top8')));
      snap.forEach(d => { if (!isPhoneDoc(d)) out.push({ url: d.data().url, visibility: 'top8' }); });
    }
  } catch { /* unauthorized tiers just return nothing */ }
  return out;
}

// ── Phone ────────────────────────────────────────────────────────────────────

// Owner editing: the phone value + its visibility. Public phone is on the doc;
// restricted phone is in the reserved restrictedSocials doc.
export async function loadOwnPhone(uid, profile) {
  if (profile?.phonePublic) return { value: profile.phonePublic, visibility: 'public' };
  try {
    const d = await getDoc(doc(db, 'users', uid, 'restrictedSocials', PHONE_ID));
    if (d.exists()) return { value: d.data().value || '', visibility: d.data().visibility || 'private' };
  } catch { /* ignore */ }
  return { value: '', visibility: 'private' };
}

// Persists the phone at the given visibility and returns the value to write to
// the user doc's `phonePublic` field (a string when public, null otherwise so
// the caller clears it). Personal accounts must pass only 'top8' or 'private'.
export async function savePhone(uid, value, visibility) {
  const ref = doc(db, 'users', uid, 'restrictedSocials', PHONE_ID);
  const v = (value || '').trim();
  if (!v) {
    try { await deleteDoc(ref); } catch { /* nothing to delete */ }
    return null;
  }
  if (visibility === 'public') {
    try { await deleteDoc(ref); } catch { /* nothing to delete */ }
    return v;
  }
  await setDoc(ref, { type: 'phone', value: v, visibility });
  return null;
}

// Viewer: the phone this viewer may see, or null. Public reads off the doc;
// restricted is a single rules-checked getDoc (denied reads simply throw).
export async function loadVisiblePhone(ownerUid, profile) {
  if (profile?.phonePublic) return { value: profile.phonePublic, visibility: 'public' };
  // Only a signed-in, entitled viewer can read a restricted phone; skip the
  // guaranteed-denied read for logged-out visitors.
  if (!auth.currentUser) return null;
  try {
    const d = await getDoc(doc(db, 'users', ownerUid, 'restrictedSocials', PHONE_ID));
    if (d.exists() && d.data().value) return { value: d.data().value, visibility: d.data().visibility || 'private' };
  } catch { /* not allowed to read it */ }
  return null;
}
